import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import { ApDocument, ApDocumentSearchCommand } from '../models/ap-document/ap-document';
import {
  WithholdingAddSourcesCommand,
  WithholdingClaimedLine,
  WithholdingDocument,
  WithholdingDocumentCreateCommand,
  WithholdingDocumentSearchCommand,
  WithholdingSaveCommand,
} from '../models/withholding-document/withholding-document';
import { PagedResult } from './bank-account.proxy.service';

/**
 * Constancia de Retención API (#4800/#5122). The Financial Officer opens a certificate for a provider and
 * ONE withholding code, attaches the AP documents it covers, decides which lines are withheld or exempt,
 * then posts it — reducing each document to net (Dr AP / Cr Retenciones por Pagar) before Treasury pays.
 *
 * Replaces ApRetentionProxyService: a retention is no longer an ApDocument, so it no longer lives under the
 * `ap/` routes and no longer returns an ApDocument.
 */
@Injectable({ providedIn: 'root' })
export class WithholdingDocumentProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/withholding-documents';

  public getById(id: string): Observable<ApiResponse<WithholdingDocument>> {
    return this.httpClient.get<ApiResponse<WithholdingDocument>>(`${this.baseUrl}/${id}`);
  }

  /**
   * The printed Comprobante de Retención (#5945) for a POSTED certificate. Returns the PDF itself rather than
   * an ApiResponse envelope, matching the other file endpoints.
   */
  public getCertificate(id: string): Observable<Blob> {
    return this.httpClient.get(`${this.baseUrl}/${id}/certificate`, { responseType: 'blob' });
  }

  /**
   * Paginated search for the certificates screen. Note there is no document-type filter to set any more —
   * the collection holds only withholding documents, so the old "hard-scope every query to Retention" hack
   * has nothing left to guard against.
   */
  public search(
    command: WithholdingDocumentSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PagedResult<WithholdingDocument>>> {
    const query = new URLSearchParams({
      pageNumber: String(pageNumber),
      pageSize: String(pageSize),
      sortOrder: String(sortOrder),
    });
    if (sortField) query.set('sortField', sortField);
    return this.httpClient.post<ApiResponse<PagedResult<WithholdingDocument>>>(`${this.baseUrl}/search?${query}`, command);
  }

  /**
   * The AP documents a certificate can still gather, for the officer's picker (#5122).
   *
   * Deliberately NOT the general AP search. Eligibility is decided server-side — posted Invoice/Receipt for
   * THIS provider, still requiring withholding (#4842), and not already fully covered by posted certificates
   * — so the picker can never offer a document `addSources` then refuses. The exclusion happens inside the
   * query, so paging and the result count stay exact rather than being thinned after the fact.
   */
  public searchEligibleDocuments(
    providerReference: string,
    command: ApDocumentSearchCommand,
    pageNumber = 1,
    pageSize = 10,
  ): Observable<ApiResponse<PagedResult<ApDocument>>> {
    const query = new URLSearchParams({
      providerReference,
      pageNumber: String(pageNumber),
      pageSize: String(pageSize),
    });
    return this.httpClient.post<ApiResponse<PagedResult<ApDocument>>>(`${this.baseUrl}/eligible-documents?${query}`, command);
  }

  /** Every certificate citing an AP document (draft + posted) — its history section. More than one is normal:
   * a document's service lines can be split across certificates under different codes. */
  public getByDocument(documentId: string): Observable<ApiResponse<WithholdingDocument[]>> {
    return this.httpClient.get<ApiResponse<WithholdingDocument[]>>(`${this.baseUrl}/by-document/${documentId}`);
  }

  /** Which of an AP document's lines are already withheld (#5122) — the greyed-out rows in the grid. */
  public getDocumentCoverage(documentId: string): Observable<ApiResponse<number[]>> {
    return this.httpClient.get<ApiResponse<number[]>>(`${this.baseUrl}/document-coverage/${documentId}`);
  }

  /** Opens — or resumes — a draft for a provider + one withholding code (#5122). */
  public create(command: WithholdingDocumentCreateCommand): Observable<ApiResponse<WithholdingDocument>> {
    return this.httpClient.post<ApiResponse<WithholdingDocument>>(this.baseUrl, command);
  }

  /** Attaches AP documents, seeding each one's still-unclaimed service lines. */
  public addSources(id: string, command: WithholdingAddSourcesCommand): Observable<ApiResponse<WithholdingDocument>> {
    return this.httpClient.post<ApiResponse<WithholdingDocument>>(`${this.baseUrl}/${id}/sources`, command);
  }

  /** Detaches one, releasing its lines to be claimed by another certificate under a different code. */
  public removeSource(id: string, documentId: string): Observable<ApiResponse<WithholdingDocument>> {
    return this.httpClient.delete<ApiResponse<WithholdingDocument>>(`${this.baseUrl}/${id}/sources/${documentId}`);
  }

  /**
   * Restores a source's lines from its AP document (#5122) — the undo for a removal that was already saved.
   * Decisions already made survive; lines a posted certificate has since claimed do not come back.
   */
  public resetSourceLines(id: string, documentId: string): Observable<ApiResponse<WithholdingDocument>> {
    return this.httpClient.post<ApiResponse<WithholdingDocument>>(`${this.baseUrl}/${id}/sources/${documentId}/reset`, {});
  }

  /** The lines another posted certificate already holds, so their absence can be explained (#5122). */
  public getSourceClaimedLines(id: string, documentId: string): Observable<ApiResponse<WithholdingClaimedLine[]>> {
    return this.httpClient.get<ApiResponse<WithholdingClaimedLine[]>>(`${this.baseUrl}/${id}/sources/${documentId}/claimed-lines`);
  }

  /** Saves the officer's per-line decisions on a draft (the server re-resolves rates + amounts). */
  public save(id: string, command: WithholdingSaveCommand): Observable<ApiResponse<WithholdingDocument>> {
    return this.httpClient.put<ApiResponse<WithholdingDocument>>(`${this.baseUrl}/${id}`, command);
  }

  /** Posts it: Dr each source's AP control / Cr Retenciones por Pagar, reducing each document to net. */
  public post(id: string): Observable<ApiResponse<WithholdingDocument>> {
    return this.httpClient.post<ApiResponse<WithholdingDocument>>(`${this.baseUrl}/${id}/post`, {});
  }
}
